import { Checkbox, Field, IconButton } from "@carbon6/c6-frontend-design";
import { ResourceBrowserLayout } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { WorkerDetailModal } from "./WorkerDetailModal";
import { WorkerJobDetailModal } from "./WorkerJobDetailModal";
import { WorkerStatusBadge } from "./WorkerStatusBadge";
import {
  useWorkerOperationsController,
  useWorkerStatusController,
} from "./useWorkerOperationsController";
import { WorkerTimeline } from "./WorkerTimeline";
import { workerTimeline } from "./workerTimelineModel";
import { formatCurrentJobKind, formatJobProgress, shortUuid } from "./workerStatusFormatters";
import { formatWorkerUnit } from "./workUnitFormat";
import { tableStripError } from "@/components/table/tableStateMessages";

export function WorkerStatusPanel() {
  const controller = useWorkerStatusController();
  const { source, snapshot, now } = controller;
  const { filters, setFilters } = useWorkerOperationsController();
  return (
    <>
      <ResourceBrowserLayout
        ariaLabel="워커 상태 목록"
        summaryCount={snapshot.total}
        summaryLabel={controller.summaryLabel}
        summaryInFilter
        // 행이 없는 조회 실패는 타임라인 본문이 알린다. 띠에는 행이 남아 있는 실패만 올린다.
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
      >
        <WorkerTimeline
          ariaLabel="Worker 타임라인"
          now={now}
          rangeKey={String(filters.liveOnly)}
          loadState={snapshot.loadState}
          errorMessage={snapshot.errorMessage}
          onRetry={() => void source.refresh()}
          emptyMessage={
            controller.liveOnly
              ? "online 상태인 워커가 없습니다."
              : "최근 12시간 내 보고한 워커가 없습니다."
          }
          summary={<FilterResultCount count={snapshot.total} unitLabel={controller.summaryLabel} />}
          actions={
            <>
              <Field label="Offline" layout="hug" labelPlacement="end">
                <Checkbox
                  checked={!filters.liveOnly}
                  aria-label="Offline"
                  onChange={(event) => setFilters({ liveOnly: !event.target.checked })}
                />
              </Field>
              <IconButton
                text="새로고침"
                size="sm"
                variant="surface"
                disabled={snapshot.loadState === "loading"}
                onClick={() => void source.refresh()}
              />
            </>
          }
          rows={snapshot.rows.map((row) => ({
            id: row.worker_id,
            name: row.worker_id,
            status: <WorkerStatusBadge online={row.online} />,
            offline: !row.online,
            summary:
              row.current_job === null
                ? "처리 중인 작업 없음"
                : [
                    row.online ? null : "마지막 작업",
                    formatCurrentJobKind(row.current_job),
                    row.current_job.subject?.dataset_display_name ??
                      shortUuid(row.current_job.uuid),
                    row.current_job.subject?.unit
                      ? formatWorkerUnit(row.current_job.subject.unit)
                      : null,
                    row.current_job.progress ? formatJobProgress(row.current_job.progress) : null,
                  ]
                    .filter(Boolean)
                    .join(" · "),
            timeline: workerTimeline(row, now),
            onOpen: () => row.onRowClick(),
          }))}
        />
      </ResourceBrowserLayout>
      <WorkerDetailModal
        workerId={controller.openedJob === null ? controller.selectedWorkerId : null}
        row={controller.selectedWorker}
        onClose={controller.closeWorker}
        onOpenJob={controller.setOpenedJob}
      />
      {controller.openedJob !== null && (
        <WorkerJobDetailModal
          key={controller.openedJob.uuid}
          kind={controller.openedJob.kind}
          uuid={controller.openedJob.uuid}
          onClose={() => controller.setOpenedJob(null)}
          onChanged={() => source.refresh()}
        />
      )}
    </>
  );
}
